import { useEffect, useState } from "react";
declare global {
    interface Window {
        globalSettings: Record<string, any>;
    }
}
const cache = new Map<FeatureID, Promise<FeatureData>>();
const loadedFeatures = new Map<FeatureID, FeatureData>();
export const FEATURES = {
    HI_PAY: "hi-pay",
    UPLOAD_ZONE: "upload-zone",
    MSH_CARD: "msh-card",
    MATOMO: "matomo",
    VALIDATIONS: "validations",
    GOOGLE_RECAPTCHA: "google-recaptcha",
    GOOGLE_MAPS: "google-maps",
    TERMS_AND_CONDITIONS: "terms-and-conditions",
    MARKETING_BANNERS: "marketing-banners",
    RADIO: "radio",
    SITEMAP: "sitemap",
    LINKEDIN_INSIGHT: "linkedin-insight",
    META_PIXEL: "meta-pixel",
    SESSION: "session",
    FACILITI: "faciliti",
    GTM: "gtm",
    CHATBOT: "chatbot",
    CONSENT: "consent",
    NOTIFICATIONS: "notifications"
} as const;

export type FeatureID = typeof FEATURES[keyof typeof FEATURES];

export const CONSENTS = {
    MATOMO: "matomo",
    FACEBOOK_PIXEL: "facebookpixel",
    LINKEDIN_INSIGHT_TAG: "linkedininsighttag",
    GOOGLE_MAPS: "googlemaps",
    YOUTUBE: "youtube",
    NP6: "np6",
    GOOGLEADS: "googleads"
} as const;

export type CONSENT_ID = typeof CONSENTS[keyof typeof CONSENTS];
//Feature data should immutable
//This method prevents object recursively to be non-writable
const deepFreeze = (obj: any): any => {
    Object.keys(obj).forEach(key => {
        const value = obj[key];
        if (typeof value === "object" && !Object.isFrozen(value)) {
            deepFreeze(value);
        }
    });
    return Object.freeze(obj);
};
//Readonly at top level and for settings object
export type FeatureData = {
    readonly feature: string;
    readonly enabled: boolean;
    readonly settings: Readonly<Record<string, any>>;
};

// Type guard function for FeatureData
const isFeatureData = (obj: any): obj is FeatureData => {
    return (
        typeof obj === "object" && // Check if it's an object
        typeof obj.feature === "string" && // Check if 'feature' is a string
        typeof obj.enabled === "boolean" && // Check if 'enabled' is a boolean
        (obj.settings === undefined || typeof obj.settings === "object") // Check if 'settings' is undefined or an object
    );
};

const defaultFeatureObject: FeatureData = {
    feature: "",
    enabled: false,
    settings: {}
};

const fetchFeature = async (featureId: FeatureID): Promise<FeatureData> => {
    try {
        const response = await fetch(`${window.globalSettings.featureEndpoint}/${featureId}`, {
            headers: {
                Accept: "application/json"
            },
            method: "GET",
            mode: "cors",
            credentials: "include"
        });
        const result = await response.json();
        if (isFeatureData(result)) {
            loadedFeatures.set(featureId, result);
            return result;
        }
    } catch (error) {
        console.error(`Failed to fetch feature ${featureId}:`, error);
    }
    return defaultFeatureObject;
};

//for direct fetching of features
export const loadFeature = (featureId: FeatureID): Promise<FeatureData> => {
    // use cache if available
    if (!cache.get(featureId)) {
        cache.set(featureId, fetchFeature(featureId));
    }
    return deepFreeze(cache.get(featureId));
};

export const getFeatureData = (featureId: FeatureID): FeatureData | undefined => {
    return loadedFeatures.get(featureId);
};

//for usage in components
export const useFetchFeature = (featureId: FeatureID) => {
    const cachedFeature = getFeatureData(featureId);
    const [loading, setLoading] = useState(!cachedFeature);
    const [info, setInfo] = useState<FeatureData>(cachedFeature || defaultFeatureObject);
    useEffect(() => {
        // use cache if available
        if (!cachedFeature) {
            if (!cache.get(featureId)) {
                cache.set(featureId, fetchFeature(featureId));
            }
            cache
                .get(featureId)
                ?.then(value => {
                    setInfo(value);
                    setLoading(false);
                })
                .catch(err => {
                    console.error(err);
                });
        }
    }, [featureId, cachedFeature]);

    return deepFreeze({ ...info, loading });
};
